import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { api } from '@/services/api'
import { normalizeApiError } from '@/services/http'
import { i18n } from '@/i18n'
import type { Category, Product } from '@/types/domain'
import type { ProductQuery } from '@/types/api'
import type { SupportedLocale } from '@/config/locales'
import { fromSlug } from '@/utils/slug'

export const useCatalogStore = defineStore('catalog', () => {
  const categories = ref<Category[]>([])
  const products = ref<Product[]>([])
  const totalProducts = ref(0)
  const categoriesLoading = ref(false)
  const productsLoading = ref(false)
  const categoriesError = ref<string | null>(null)
  const productsError = ref<string | null>(null)
  const categoriesLocale = ref<SupportedLocale | null>(null)
  let categoriesController: AbortController | null = null
  let categoriesPromise: Promise<Category[]> | null = null
  let categoriesPromiseLocale: SupportedLocale | null = null
  let productsController: AbortController | null = null

  const categoriesLoaded = computed(
    () => categories.value.length > 0 && categoriesLocale.value === i18n.global.locale.value,
  )

  const fetchCategories = async (force = false): Promise<Category[]> => {
    const requestLocale = i18n.global.locale.value as SupportedLocale

    if (!force && categories.value.length && categoriesLocale.value === requestLocale) {
      return categories.value
    }

    if (!force && categoriesPromise && categoriesPromiseLocale === requestLocale) {
      return categoriesPromise
    }

    categoriesController?.abort()
    categoriesController = new AbortController()
    const controller = categoriesController
    categoriesPromiseLocale = requestLocale
    categoriesLoading.value = true
    categoriesError.value = null

    categoriesPromise = api
      .getCategories(controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return []

        const responseCategories = Array.isArray(response.categories) ? response.categories : []
        categories.value = responseCategories
        categoriesLocale.value = requestLocale
        return responseCategories
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return []
        categoriesError.value = normalizeApiError(error).message
        return []
      })
      .finally(() => {
        if (categoriesController === controller) {
          categoriesLoading.value = false
          categoriesPromise = null
          categoriesPromiseLocale = null
        }
      })

    return categoriesPromise
  }

  const fetchProducts = async (query: ProductQuery): Promise<Product[]> => {
    productsController?.abort()
    productsController = new AbortController()
    const controller = productsController
    productsLoading.value = true
    productsError.value = null
    products.value = []

    try {
      const response = await api.getProducts(query, controller.signal)
      if (controller.signal.aborted) return []

      const responseProducts = Array.isArray(response.products) ? response.products : []
      products.value = responseProducts
      totalProducts.value = Number.isFinite(response.count) ? response.count : responseProducts.length
      return responseProducts
    } catch (error) {
      if (controller.signal.aborted) return []
      productsError.value = normalizeApiError(error).message
      return []
    } finally {
      if (productsController === controller) productsLoading.value = false
    }
  }

  const fetchProduct = async (productId: number, signal?: AbortSignal): Promise<Product | null> => {
    try {
      const response = await api.getProducts({ product_id: productId }, signal)
      return Array.isArray(response.products) ? (response.products[0] ?? null) : null
    } catch (error) {
      if (signal?.aborted) return null
      productsError.value = normalizeApiError(error).message
      return null
    }
  }

  const getCategoryIdBySlug = (slug?: string): number | undefined => {
    if (!slug) return undefined
    const name = fromSlug(slug).toLowerCase()
    return categories.value.find((category) => category.name.toLowerCase() === name)?.id
  }

  const getManufacturerIdBySlug = (categoryId: number | undefined, slug?: string): number | undefined => {
    if (!categoryId || !slug) return undefined
    const name = fromSlug(slug).toLowerCase()
    return categories.value
      .find((category) => category.id === categoryId)
      ?.manufacturers.find((manufacturer) => manufacturer.name.toLowerCase() === name)?.id
  }

  const clearProducts = (): void => {
    productsController?.abort()
    products.value = []
    totalProducts.value = 0
  }

  return {
    categories,
    products,
    totalProducts,
    categoriesLoading,
    productsLoading,
    categoriesError,
    productsError,
    categoriesLocale,
    categoriesLoaded,
    fetchCategories,
    fetchProducts,
    fetchProduct,
    getCategoryIdBySlug,
    getManufacturerIdBySlug,
    clearProducts,
  }
})
